import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, Share2, BookOpen, History } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { archetypeResults } from '../data/archetypeData';
import { trackCompleteTest } from '../utils/metaPixel';
import { mapaArquetiposCapitulos, getColdSeasons, getDeepRecommendations, getArchetypeDisplayName } from '../data/mapData';
import { tensaoQueTeMove, perguntaClinica, defesaCaracteristica, getChapterContent, PALETA_OFICIAL } from '../data/bookContent';
import { TENSAO_RAIZ } from '../data/archetypeContent';
import { computeResult } from '../utils/scoringEngine';
import { questions } from '../data/questionsData';
import MapaEstacoes from './MapaEstacoes';
import DinamicaEstacoes from './DinamicaEstacoes';
import EvolucaoEstacoes from './EvolucaoEstacoes';
import SeloConfiabilidade from './SeloConfiabilidade';
import ResultadoTransicao from './ResultadoTransicao';
import CartaDaEstacao from './CartaDaEstacao';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============ FUNÇÕES DE INTERPRETAÇÃO ============
const generateAccessibleInterpretation = (result) => {
  const { mainArchetypes, dominantArchetype, archetypeData } = result;
  
  const scores = mainArchetypes;
  
  const sortedArchetypes = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const [strongest, strongestScore] = sortedArchetypes[0];
  const [secondStrong] = sortedArchetypes[1];
  const [weakest, weakestScore] = sortedArchetypes[3];
  
  const archetypeNames = {
    peregrino: "Peregrino",
    guardiao: "Guardião", 
    amante: "Amante",
    alquimista: "Alquimista"
  };
  
  const archetypeEmojis = {
    peregrino: "🌿",
    guardiao: "🔥",
    amante: "💧",
    alquimista: "🌙"
  };
  
  // Usa conteúdo personalizado do ARQUÉTIPO DOMINANTE
  // (description já está no card principal, aqui usamos os textos complementares)
  const camada_emocional = {
    introducao_suave: archetypeData?.prosAcolhedor || `Você tem uma forma especial de experimentar e responder à vida, guiada pela energia do ${archetypeNames[strongest] || strongest}.`,
    descricao_essencial: archetypeData?.contrasAcolhedor || '',
    mensagem_inspiradora: archetypeData?.conselhoAcolhedor || '',
    praticas: archetypeData?.praticasAcolhedoras || []
  };
  
  const range = strongestScore - weakestScore;
  let estado_integracao = 'integro';
  if (range > 15) estado_integracao = 'fragmentado';
  else if (range > 8) estado_integracao = 'quase_integro';
  
  const simbolos_ativos = sortedArchetypes
    .filter(([_, score]) => score >= 20)
    .map(([arch, _]) => archetypeEmojis[arch]);
  
  const camada_visual = {
    estado_integracao,
    mapa_do_coracao: {
      presenca: scores.peregrino,
      acao: scores.guardiao,
      alma: scores.alquimista,
      sentimento: scores.amante
    },
    simbolos_ativos: simbolos_ativos.length > 0 ? simbolos_ativos : [archetypeEmojis[strongest]]
  };
  
  const camada_clareza = {
    como_voce_sente: `Você tende a processar suas experiências principalmente através da lente do ${archetypeNames[strongest]}, com influência secundária do ${archetypeNames[secondStrong]}.`,
    como_voce_age: `Suas ações são guiadas por ${strongestScore > 25 ? 'uma forte tendência' : 'uma inclinação natural'} para ${strongest === 'peregrino' ? 'buscar movimento e novidade' : strongest === 'guardiao' ? 'criar estrutura e segurança' : strongest === 'amante' ? 'conectar-se profundamente' : 'encontrar significado profundo'}.`,
    o_que_esta_crescendo: `A dimensão do ${archetypeNames[weakest]} pede mais atenção em sua vida. Esta é sua área de maior potencial de crescimento.`,
    o_que_te_equilibra: `O equilíbrio vem de honrar seu ${archetypeNames[strongest]} dominante enquanto cultiva conscientemente seu ${archetypeNames[weakest]}.`
  };
  
  const camada_evolutiva = {
    mensagem: `Sua jornada única combina a força do ${archetypeNames[strongest]} com oportunidades de crescimento através do ${archetypeNames[weakest]}. Lembre-se: cada arquétipo tem seus dons e desafios. O caminho não é eliminar quem você é, mas integrar todas as partes.`,
    sugestao_pratica: archetypeData.apoio ? archetypeData.apoio[0] : "Dedique alguns minutos por dia para observar como você reage às situações. Apenas observe, sem julgar."
  };
  
  return {
    camada_emocional,
    camada_visual,
    camada_clareza,
    camada_evolutiva
  };
};

const generateTextualInterpretation = (result) => {
  const { mainArchetypes, dominantArchetype } = result;
  
  const archetypeNames = {
    peregrino: "Peregrino",
    guardiao: "Guardião",
    amante: "Amante",
    alquimista: "Alquimista"
  };
  
  const sortedMain = Object.entries(mainArchetypes).sort((a, b) => b[1] - a[1]);
  const [primary, primaryScore] = sortedMain[0];
  const [secondary, secondaryScore] = sortedMain[1];
  
  let interpretation = `**Análise do Perfil Emocional**\n\n`;
  interpretation += `Seu coração manifesta predominantemente a energia do **${archetypeNames[primary]}** (${primaryScore} pontos), `;
  interpretation += `com forte influência do **${archetypeNames[secondary]}** (${secondaryScore} pontos).\n\n`;
  
  const range = sortedMain[0][1] - sortedMain[3][1];
  if (range <= 8) {
    interpretation += `Seu perfil mostra um **equilíbrio notável** entre as dimensões. Você tem acesso relativamente igual a todas as formas de experiência do coração.\n\n`;
  } else if (range <= 15) {
    interpretation += `Seu perfil mostra **tendências claras** mas com boa integração. Você tem preferências definidas mas mantém flexibilidade.\n\n`;
  } else {
    interpretation += `Seu perfil mostra **polarização significativa**. Isso pode indicar tanto uma especialização natural quanto áreas que pedem mais atenção e desenvolvimento.\n\n`;
  }
  
  return interpretation;
};

const generatePracticalInterpretation = (result) => {
  const { mainArchetypes, archetypeData } = result;
  
  const sortedMain = Object.entries(mainArchetypes).sort((a, b) => b[1] - a[1]);
  const [strongest] = sortedMain[0];
  const [weakest] = sortedMain[3];
  
  const strengths = {
    peregrino: ["Adaptabilidade natural às mudanças", "Coragem para novos começos", "Visão de longo prazo", "Capacidade de renovação"],
    guardiao: ["Confiabilidade e consistência", "Capacidade de criar estruturas", "Força para proteger o que ama", "Disciplina natural"],
    amante: ["Capacidade de conexão profunda", "Empatia desenvolvida", "Generosidade afetiva", "Presença acolhedora"],
    alquimista: ["Capacidade de encontrar sentido", "Sabedoria nascida da experiência", "Resiliência emocional", "Profundidade de compreensão"]
  };
  
  const challenges = {
    peregrino: ["Dificuldade em criar raízes", "Tendência a evitar compromissos profundos", "Risco de confundir movimento com progresso"],
    guardiao: ["Rigidez diante de mudanças", "Tendência ao controle excessivo", "Dificuldade em delegar"],
    amante: ["Tendência a se perder no outro", "Dificuldade com limites", "Medo de abandono"],
    alquimista: ["Tendência ao isolamento", "Risco de intelectualizar emoções", "Dificuldade com a leveza"]
  };
  
  const development = {
    peregrino: ["Praticar a permanência consciente", "Cultivar relacionamentos de longo prazo", "Celebrar chegadas, não só partidas"],
    guardiao: ["Praticar pequenas aberturas diárias", "Delegar uma tarefa por semana", "Meditar sobre a impermanência"],
    amante: ["Estabelecer limites amorosos", "Praticar autocuidado primeiro", "Desenvolver projetos individuais"],
    alquimista: ["Buscar alegrias simples", "Praticar presença sem análise", "Conectar-se através do corpo"]
  };
  
  return {
    mainStrengths: strengths[strongest] || [],
    mainChallenges: challenges[strongest] || [],
    developmentAreas: development[weakest] || [],
    advice: archetypeData.conselho || "Continue explorando as diferentes dimensões do seu coração."
  };
};

// ============ COMPONENTE PRINCIPAL ============
const ResultadoPage = () => {
  const { sessionId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showEvolucao, setShowEvolucao] = useState(false);
  const resultRef = useRef(null);
  const bussolaRef = useRef(null);
  const [sharingBussola, setSharingBussola] = useState(false);

  const handleDownloadBussola = async () => {
    const element = bussolaRef.current;
    if (!element) return;
    setSharingBussola(true);
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#1e1b4b',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'minha-bussola-viva.png';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao gerar imagem:', err);
    } finally {
      setSharingBussola(false);
    }
  };

  const handleShareLink = async () => {
    const shareText = `Descobri meu arquétipo emocional: ${result?.dominantArchetypeName || result?.archetypeData?.titulo || ''}. Descubra o seu em:`;
    const shareUrl = 'https://estacoes.ocoracaodetodasascoisas.com';
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Minha Bússola Viva', text: shareText, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        alert('Link copiado!');
      }
    } catch (_) {
      try {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        alert('Link copiado!');
      } catch (__) {}
    }
  };

  useEffect(() => {
    const fetchResult = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/test-results/${sessionId}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Resultado não encontrado');
          }
          throw new Error('Erro ao carregar resultado');
        }
        
        const data = await response.json();
        
        // Normalizar dados (compatível com ambos os schemas)
        const mainArchetypes = {
          peregrino: data.score_peregrino || data.score_primavera || 0,
          guardiao: data.score_guardiao || data.score_inverno || 0,
          amante: data.score_amante || data.score_verao || 0,
          alquimista: data.score_alquimista || data.score_outono || 0
        };
        
        const dominantArchetype = data.arquetipo_dominante || data.estacao_dominante || 'peregrino';
        
        // Mapear combinações do banco (snake_case) para formato interno (camelCase)
        const dualCombinations = data.combinacoes_duais || { visionario: 0, encontro: 0, guerreiro: 0, curador: 0, guardiao_sabio: 0, poeta: 0 };
        const tripleCombinations = data.combinacoes_triplas || { profeta: 0, construtor: 0, companheiro: 0, sabio_compassivo: 0 };
        
        // Recalcular usando o motor novo para obter confidence, combos, etc.
        // 🔴 FIX: Só recalcular se for resultado da v2 (tem item de atenção id 61)
        // Resultados antigos tinham itens 7,8,15,16,23,24,31,32 como positivos,
        // mas na v2 viraram reversos - recalcular geraria escore errado
        let computedResult = {};
        const ehV2 = data.respostas && data.respostas['61'] != null;
        if (ehV2) {
          try {
            computedResult = computeResult(data.respostas, questions);
          } catch (e) {
            console.warn('Não foi possível recalcular resultado:', e);
          }
        }
        
        setResult({
          ...data,
          ...computedResult,
          mainArchetypes: computedResult.mainArchetypes || mainArchetypes,
          dualCombinations: computedResult.dualCombinations || dualCombinations,
          tripleCombinations: computedResult.tripleCombinations || tripleCombinations,
          dominantArchetype: computedResult.dominantArchetype || dominantArchetype,
          archetypeData: archetypeResults[computedResult.displayKey || dominantArchetype] || archetypeResults.peregrino,
          isCoracaoInteiro: computedResult.isCoracaoInteiro || data.is_coracao_inteiro || false,
          nome_identificador: data.nome_identificador || data.nome || ''
        });
        
        // Disparar evento do Meta Pixel ao carregar resultado
        trackCompleteTest(dominantArchetype, sessionId);
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (sessionId) {
      fetchResult();
    }
  }, [sessionId]);

  // Geração de PDF via html2canvas + jsPDF (captura o design exato da página)
  const handleDownloadPDF = async () => {
    if (!resultRef.current) return;
    
    setGeneratingPDF(true);
    toast({ title: "Gerando PDF...", description: "Aguarde alguns segundos." });
    
    try {
      // Capturar o conteúdo da página
      const canvas = await html2canvas(resultRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#1e1b4b', // bg-indigo-950
        logging: false,
        windowWidth: 1200,
        windowHeight: resultRef.current.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Criar PDF em A4
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calcular dimensões mantendo proporção
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const finalWidth = imgWidth * ratio * 0.95;
      const finalHeight = imgHeight * ratio * 0.95;
      
      // Centralizar na página
      const x = (pdfWidth - finalWidth) / 2;
      const y = 5;
      
      // Se o conteúdo for maior que uma página, dividir em múltiplas páginas
      const pageHeight = pdfHeight - 10; // margem
      const totalPages = Math.ceil(finalHeight / pageHeight);
      
      if (totalPages <= 1) {
        pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
      } else {
        // Múltiplas páginas
        let remainingHeight = finalHeight;
        let currentY = 0;
        
        for (let page = 0; page < totalPages; page++) {
          if (page > 0) {
            pdf.addPage();
          }
          
          const sliceHeight = Math.min(pageHeight, remainingHeight);
          
          // Criar canvas para esta fatia
          const pageCanvas = document.createElement('canvas');
          const sourceY = (currentY / finalHeight) * imgHeight;
          const sourceHeight = (sliceHeight / finalHeight) * imgHeight;
          
          pageCanvas.width = imgWidth;
          pageCanvas.height = sourceHeight;
          
          const ctx = pageCanvas.getContext('2d');
          ctx.drawImage(
            canvas,
            0, sourceY, imgWidth, sourceHeight,
            0, 0, imgWidth, sourceHeight
          );
          
          const pageImgData = pageCanvas.toDataURL('image/png');
          const pageImgHeight = (sourceHeight / imgWidth) * finalWidth;
          
          pdf.addImage(pageImgData, 'PNG', x, y, finalWidth, pageImgHeight);
          
          currentY += sliceHeight;
          remainingHeight -= sliceHeight;
        }
      }
      
      // Adicionar rodapé em todas as páginas
      const totalPdfPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPdfPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text('O Coração de Todas as Coisas - www.ocoracaodetodasascoisas.com', pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }
      
      // Download
      const fileName = `resultado_coracao_${result?.nome_identificador || 'teste'}_${sessionId.slice(0, 8)}.pdf`;
      pdf.save(fileName);
      
      toast({ title: "PDF gerado!", description: "Seu resultado foi salvo com sucesso." });
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      toast({ title: "Erro", description: "Não foi possível gerar o PDF.", variant: "destructive" });
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-[#0a1628] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/80">Carregando resultado...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-[#0a1628] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">😔</div>
          <h1 className="text-2xl font-bold text-white mb-2">Resultado não encontrado</h1>
          <p className="text-white/70 mb-6">{error}</p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-all"
          >
            <ArrowLeft size={20} />
            Fazer o teste
          </Link>
        </div>
      </div>
    );
  }

  if (!result) return null;

  const archetype = result.archetypeData;
  const accessible = generateAccessibleInterpretation(result);
  const practical = generatePracticalInterpretation(result);

  const estadoMessages = {
    integro: 'O círculo pulsa suavemente em harmonia.',
    quase_integro: 'O círculo respira com leve variação entre as partes.',
    fragmentado: 'Algumas partes brilham mais que outras — sinal de crescimento em andamento.'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-[#0a1628] py-8 px-4" data-testid="resultado-page">
      <div className="max-w-4xl mx-auto" ref={resultRef}>
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Resultado do Teste
          </h1>
          {result.nome_identificador && (
            <p className="text-white/70">de {result.nome_identificador}</p>
          )}
        </div>

        {/* Main Result Card - ATUALIZADO COM NOVAS ARTES */}
        <div className="bg-gradient-to-br from-indigo-900/50 via-slate-900/60 to-purple-900/50 backdrop-blur-sm rounded-2xl p-6 border border-amber-500/20 mb-6 shadow-2xl">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Archetype Image - Nova Arte */}
            <div className="flex-shrink-0 relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-transparent rounded-xl blur-xl group-hover:blur-2xl transition-all duration-500" />
              <img 
                src={`/images/arquetipos/${result.dominantArchetype}.png`}
                alt={archetype.title}
                className="relative w-56 h-auto md:w-64 object-contain rounded-xl border-2 border-amber-500/30 shadow-xl group-hover:border-amber-400/50 transition-all duration-300"
                onError={(e) => {
                  // Fallback para imagem antiga se nova não existir
                  e.target.src = archetype.image;
                  e.target.onerror = () => { e.target.style.display = 'none'; };
                }}
              />
            </div>
            
            <div className="text-center md:text-left flex-grow">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-1 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">{archetype.title}</h2>
              <p className="text-amber-200/70 mb-3 text-lg">{archetype.subtitle}</p>
              
              {result.isCoracaoInteiro && (
                <div className="inline-block px-4 py-2 bg-gradient-to-r from-amber-500/20 to-purple-500/20 rounded-full mb-3 border border-amber-500/30">
                  <span className="text-amber-200 text-sm font-medium">✨ Integração Completa das Estações</span>
                </div>
              )}
              
              <p className="text-white/80 leading-relaxed">
                {archetype.description}
              </p>
            </div>
          </div>
        </div>

        {/* ====== SELO DE CONFIABILIDADE ====== */}
        {result.confidence && (
          <div className="mb-6">
            <SeloConfiabilidade confidence={result.confidence} />
          </div>
        )}

        {/* ====== TRANSIÇÃO (se aplicável) ====== */}
        {result.dominant?.type === 'transition' && <ResultadoTransicao result={result} />}

        {/* ====== CARTA DA ESTAÇÃO ====== */}
        <CartaDaEstacao result={result} nome={result.nome_identificador} />

        {/* ====== TÍTULO DA SEÇÃO ACOLHEDORA ====== */}
        <div className="flex justify-center mb-6">
          <div className="bg-gradient-to-r from-purple-900/50 via-pink-900/50 to-purple-900/50 px-6 py-3 rounded-full border border-purple-500/20">
            <div className="flex items-center gap-3">
              <span className="text-2xl">💝</span>
              <div className="text-center">
                <h3 className="text-2xl md:text-3xl font-extrabold text-white">Visão Acolhedora</h3>
                <p className="text-white/60 text-xs">Reflexões do coração</p>
              </div>
            </div>
          </div>
        </div>

        {/* ====== VERSÃO ACOLHEDORA ====== */}
          <div className="space-y-6">
            {/* CAMADA 1: Emocional */}
            <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">💝</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">A Linguagem do Seu Coração</h3>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-4">
                <p className="text-white/90 leading-relaxed text-lg">{accessible.camada_emocional.introducao_suave}</p>
                {accessible.camada_emocional.descricao_essencial && (
                  <p className="text-white/80 leading-relaxed">{accessible.camada_emocional.descricao_essencial}</p>
                )}
                {accessible.camada_emocional.mensagem_inspiradora && (
                  <p className="text-white/90 leading-relaxed italic border-t border-white/10 pt-4">{accessible.camada_emocional.mensagem_inspiradora}</p>
                )}
                {accessible.camada_emocional.praticas?.length > 0 && (
                  <div className="border-t border-white/10 pt-4 space-y-3">
                    <p className="text-white/60 text-sm font-medium uppercase tracking-wider">Práticas sugeridas</p>
                    {accessible.camada_emocional.praticas.map((pratica, i) => (
                      <p key={i} className="text-white/70 text-sm leading-relaxed pl-3 border-l-2 border-purple-500/30">
                        {pratica}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* CAMADA 2: Visual - Mapa do Coração */}
            <div className="space-y-6">
              {/* Header estilizado como botão */}
              <div className="flex justify-center">
                <div className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-900/60 to-indigo-900/60 border border-purple-500/30 shadow-lg">
                  <span className="text-2xl">💜</span>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      Mapa do Seu Coração
                    </h3>
                    <p className="text-white/50 text-sm">
                      {estadoMessages[accessible.camada_visual.estado_integracao]}
                    </p>
                  </div>
                </div>
              </div>

              {/* Gráfico de Pizza + Legenda em caixinhas */}
              {(() => {
                const mc = accessible.camada_visual.mapa_do_coracao;
                
                // Cores e emojis
                const CORES = {
                  amante: '#EC4899',
                  alquimista: '#F59E0B', 
                  peregrino: '#22C55E',
                  guardiao: '#A855F7',
                };
                const EMOJIS = {
                  amante: '💕',
                  alquimista: '✨', 
                  peregrino: '🌿',
                  guardiao: '🏠',
                };
                
                const segments = [
                  { key: 'amante', label: 'Amante', sublabel: 'Vínculo', score: mc.sentimento },
                  { key: 'alquimista', label: 'Alquimista', sublabel: 'Sentido', score: mc.alma },
                  { key: 'peregrino', label: 'Peregrino', sublabel: 'Movimento', score: mc.presenca },
                  { key: 'guardiao', label: 'Guardião', sublabel: 'Estabilidade', score: mc.acao },
                ];
                
                const total = segments.reduce((sum, s) => sum + s.score, 0);
                const minScore = Math.min(...segments.map(s => s.score));
                const maxScore = Math.max(...segments.map(s => s.score));

                // Calcular ângulos para o gráfico de pizza
                let currentAngle = 0;
                const computed = segments.map(seg => {
                  const pct = Math.round((seg.score / 40) * 100);
                  const sliceAngle = (seg.score / total) * 360;
                  const startAngle = currentAngle;
                  const midAngle = currentAngle + sliceAngle / 2;
                  currentAngle += sliceAngle;
                  const isWeakest = seg.score === minScore && segments.filter(s => s.score === minScore).length < 4;
                  const isDominant = seg.score === maxScore;
                  
                  // Posição do texto dentro da fatia
                  const textRadius = 30;
                  const textAngleRad = (midAngle - 90) * Math.PI / 180;
                  const textX = 50 + textRadius * Math.cos(textAngleRad);
                  const textY = 50 + textRadius * Math.sin(textAngleRad);
                  
                  return { ...seg, pct, startAngle, sliceAngle, midAngle, isWeakest, isDominant, textX, textY };
                });

                // Função para criar path de fatia de pizza
                const createSlicePath = (startAngle, sliceAngle, radius = 40) => {
                  const startRad = (startAngle - 90) * Math.PI / 180;
                  const endRad = (startAngle + sliceAngle - 90) * Math.PI / 180;
                  const x1 = 50 + radius * Math.cos(startRad);
                  const y1 = 50 + radius * Math.sin(startRad);
                  const x2 = 50 + radius * Math.cos(endRad);
                  const y2 = 50 + radius * Math.sin(endRad);
                  const largeArc = sliceAngle > 180 ? 1 : 0;
                  return `M 50 50 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
                };

                const dominante = computed.find(s => s.isDominant) || computed[0];

                return (
                  <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
                    {/* Gráfico de Pizza */}
                    <div className="relative w-56 h-56 md:w-64 md:h-64">
                      <svg viewBox="0 0 100 100" className="w-full h-full">
                        {/* Fatias da pizza */}
                        {computed.map(seg => (
                          <path
                            key={seg.key}
                            d={createSlicePath(seg.startAngle, seg.sliceAngle)}
                            fill={seg.isWeakest ? 'rgba(100, 116, 139, 0.3)' : CORES[seg.key]}
                            opacity={seg.isWeakest ? 0.5 : 0.85}
                            stroke="rgba(15, 23, 42, 0.5)"
                            strokeWidth="0.5"
                            className="transition-all duration-500"
                          />
                        ))}
                        
                        {/* Percentuais dentro de cada fatia */}
                        {computed.map(seg => (
                          <text
                            key={`pct-${seg.key}`}
                            x={seg.textX}
                            y={seg.textY}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill="white"
                            fontSize="6"
                            fontWeight="bold"
                            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                          >
                            {seg.pct}%
                          </text>
                        ))}
                        
                        {/* Círculo central */}
                        <circle cx="50" cy="50" r="18" fill="rgba(15, 23, 42, 0.95)" />
                        <circle cx="50" cy="50" r="17" fill="rgba(30, 41, 59, 0.9)" />
                      </svg>

                      {/* Emoji e nome do dominante no centro */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-2xl mb-0.5">
                            {EMOJIS[dominante.key]}
                          </div>
                          <div className="text-lg md:text-xl font-bold text-white">
                            {dominante.pct}%
                          </div>
                          <div className="text-[10px] text-white/60">
                            {dominante.label}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Legenda em caixinhas separadas */}
                    <div className="flex flex-col gap-3 w-full max-w-sm">
                      {computed.map(seg => (
                        <div 
                          key={seg.key}
                          className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                            seg.isDominant 
                              ? 'bg-slate-700/60 border-slate-500/40' 
                              : 'bg-slate-800/30 border-slate-700/30'
                          }`}
                          data-testid={`legend-${seg.key}`}
                        >
                          {/* Indicador circular colorido */}
                          <div 
                            className="w-4 h-4 rounded-full flex-shrink-0"
                            style={{ 
                              backgroundColor: seg.isWeakest ? 'rgba(100, 116, 139, 0.4)' : CORES[seg.key]
                            }}
                          />
                          
                          {/* Info */}
                          <div className="flex-1">
                            <div className="flex items-baseline gap-2">
                              <span className={`text-lg font-bold ${seg.isWeakest ? 'text-slate-500' : 'text-white'}`}>
                                {seg.pct}%
                              </span>
                              <span className={`text-sm ${seg.isWeakest ? 'text-slate-500' : 'text-white/80'}`}>
                                {seg.label}
                              </span>
                            </div>
                            <div className="text-xs text-white/40">
                              {seg.sublabel}
                            </div>
                          </div>
                          
                          {/* Badge */}
                          {seg.isDominant && (
                            <span className="text-xs text-white/70 bg-slate-600/60 px-3 py-1.5 rounded-lg border border-slate-500/30">
                              dominante
                            </span>
                          )}
                          {seg.isWeakest && (
                            <span className="text-xs text-amber-400/80 flex items-center gap-1">
                              🌙 atenção
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* ====== A TENSÃO QUE TE MOVE ====== */}
            {(() => {
              const dominantKey = result.dominantArchetype;
              const isRaiz = ['peregrino', 'guardiao', 'amante', 'alquimista'].includes(dominantKey);
              
              // TENSAO_RAIZ é string direta, tensaoQueTeMove é objeto com .texto
              const tensaoTexto = isRaiz 
                ? TENSAO_RAIZ[dominantKey] 
                : tensaoQueTeMove[dominantKey]?.texto;
              
              if (!tensaoTexto) return null;
              return (
                <div className="bg-gradient-to-br from-rose-900/20 via-slate-900/30 to-indigo-900/20 backdrop-blur-sm rounded-2xl p-6 border border-rose-500/15" data-testid="tensao-que-te-move">
                  <div className="text-center mb-4">
                    <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2">
                      A Tensão que te Move
                    </h3>
                    <p className="text-white/50 text-sm">O conflito central do seu coração</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                    <p className="text-white/90 text-lg leading-relaxed italic text-center">
                      "{tensaoTexto}"
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* VERSÃO ACOLHEDORA - Pontos Fortes e Áreas de Atenção */}
            <div className="bg-green-900/30 backdrop-blur-sm rounded-xl p-5 border border-green-500/30">
              <h3 className="text-2xl md:text-3xl font-extrabold text-green-200 mb-3 flex items-center gap-2">
                <span>💪</span> Seus Pontos Fortes
              </h3>
              <p className="text-white/90 leading-relaxed">{archetype.prosAcolhedor || archetype.pros}</p>
            </div>

            <div className="bg-amber-900/30 backdrop-blur-sm rounded-xl p-5 border border-amber-500/30">
              <h3 className="text-2xl md:text-3xl font-extrabold text-amber-200 mb-3 flex items-center gap-2">
                <span>⚡</span> A Sombra
              </h3>
              <p className="text-white/90 leading-relaxed">{archetype.contrasAcolhedor || archetype.contras}</p>
              {defesaCaracteristica[result.dominantArchetype] && (
                <div className="mt-4 pt-4 border-t border-amber-500/20">
                  <p className="text-amber-200/70 text-xs font-semibold uppercase tracking-wider mb-2">O que você evita sentir</p>
                  <p className="text-white/80 leading-relaxed italic">
                    {defesaCaracteristica[result.dominantArchetype]}
                  </p>
                </div>
              )}
            </div>

            <div className="bg-purple-900/30 backdrop-blur-sm rounded-xl p-5 border border-purple-500/30">
              <h3 className="text-2xl md:text-3xl font-extrabold text-purple-200 mb-3 flex items-center gap-2">
                <span>💡</span> Reflexão
              </h3>
              <p className="text-white/90 leading-relaxed italic text-lg">
                "{perguntaClinica[result.dominantArchetype] || archetype.conselhoAcolhedor || archetype.conselho}"
              </p>
            </div>

            {/* Práticas Simples - Versão Acolhedora */}
            {archetype.praticasAcolhedoras && (
              <div className="bg-gradient-to-br from-teal-900/30 to-cyan-900/30 backdrop-blur-sm rounded-2xl p-6 border border-teal-500/20">
                <div className="text-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">🌱</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Práticas para o Dia a Dia</h3>
                  <p className="text-white/60 text-sm">Exercícios simples para cultivar seu caminho</p>
                </div>
                <div className="space-y-3">
                  {archetype.praticasAcolhedoras.map((pratica, index) => (
                    <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-teal-300 font-bold text-sm">{index + 1}</span>
                        </div>
                        <p className="text-white/90 leading-relaxed">{pratica}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* NÃO mostrar perspectivas técnicas na versão acolhedora */}
            {false && archetype.neuroPerspective && (
              <div className="bg-gradient-to-br from-indigo-900/40 to-blue-900/40 backdrop-blur-sm rounded-2xl p-6 border border-indigo-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-blue-400 rounded-full flex items-center justify-center">
                    <span className="text-xl">🧠</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Perspectiva Neuropsicológica</h3>
                    <p className="text-white/50 text-xs">Bases neurais do seu padrão</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/85 leading-relaxed text-sm">{archetype.neuroPerspective}</p>
                </div>
              </div>
            )}

            {/* Camada Evolutiva - Versão Acolhedora */}
            <div className="bg-gradient-to-br from-rose-900/30 to-pink-900/30 backdrop-blur-sm rounded-2xl p-6 border border-rose-500/20">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-white mb-2">🌱 O Caminho do Desenvolvimento</h3>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-4">
                <p className="text-white/90 leading-relaxed">{accessible.camada_evolutiva.mensagem}</p>
                <div className="border-t border-white/10 pt-4">
                  <p className="text-rose-200 font-medium mb-2">Sugestão Prática:</p>
                  <p className="text-white/80 italic">{accessible.camada_evolutiva.sugestao_pratica}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ====== DIVISOR VISUAL ENTRE AS SEÇÕES ====== */}
          <div className="relative my-12">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/20"></div>
            </div>
            <div className="relative flex justify-center">
              <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-900 px-6 py-3 rounded-full border border-white/20">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📊</span>
                  <div className="text-center">
                    <h3 className="text-2xl md:text-3xl font-extrabold text-white">Visão Analítica</h3>
                    <p className="text-white/60 text-xs">Perspectiva Neuropsicológica</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ====== VERSÃO ANALÍTICA ====== */}
          <div className="space-y-6">
            {/* Perspectiva Neuropsicológica */}
            {archetype.neuroPerspective && (
              <div className="bg-gradient-to-br from-indigo-900/40 to-blue-900/40 backdrop-blur-sm rounded-2xl p-6 border border-indigo-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-blue-400 rounded-full flex items-center justify-center">
                    <span className="text-xl">🧠</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Perspectiva Neuropsicológica</h3>
                    <p className="text-white/50 text-xs">Bases neurais do seu padrão emocional</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/85 leading-relaxed text-sm">{archetype.neuroPerspective}</p>
                </div>
              </div>
            )}

            {/* Perspectiva Fenomenológica */}
            {archetype.fenoPerspective && (
              <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                    <span className="text-xl">🌸</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Perspectiva Fenomenológica</h3>
                    <p className="text-white/50 text-xs">A experiência vivida do seu arquétipo</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/85 leading-relaxed text-sm">{archetype.fenoPerspective}</p>
                </div>
              </div>
            )}

            {/* Pontos Fortes Analíticos */}
            {archetype.prosAnalitico && (
              <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 backdrop-blur-sm rounded-2xl p-6 border border-green-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center">
                    <span className="text-xl">💪</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Seus Pontos Fortes</h3>
                    <p className="text-white/50 text-xs">Recursos e capacidades do seu padrão</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/85 leading-relaxed text-sm">{archetype.prosAnalitico}</p>
                </div>
              </div>
            )}

            {/* Áreas de Atenção Analíticas */}
            {archetype.contrasAnalitico && (
              <div className="bg-gradient-to-r from-amber-900/40 to-orange-900/40 backdrop-blur-sm rounded-2xl p-6 border border-amber-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center">
                    <span className="text-xl">⚠️</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Áreas de Atenção</h3>
                    <p className="text-white/50 text-xs">Desafios e riscos do seu padrão</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/85 leading-relaxed text-sm">{archetype.contrasAnalitico}</p>
                </div>
              </div>
            )}

            {/* Sombra do Arquétipo */}
            {archetype.sombra && (
              <div className="bg-gradient-to-r from-slate-900/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-slate-400 to-gray-400 rounded-full flex items-center justify-center">
                    <span className="text-xl">🌑</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">A Sombra</h3>
                    <p className="text-white/50 text-xs">O lado oculto do seu arquétipo</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/85 leading-relaxed text-sm">{archetype.sombra}</p>
                </div>
              </div>
            )}

            {/* Dinâmica Relacional */}
            {archetype.dinamicaRelacional && (
              <div className="bg-gradient-to-r from-rose-900/40 to-pink-900/40 backdrop-blur-sm rounded-2xl p-6 border border-rose-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-400 rounded-full flex items-center justify-center">
                    <span className="text-xl">💞</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Dinâmica Relacional</h3>
                    <p className="text-white/50 text-xs">Como você se relaciona com os outros</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/85 leading-relaxed text-sm">{archetype.dinamicaRelacional}</p>
                </div>
              </div>
            )}

            {/* Manifestação no Trabalho */}
            {archetype.manifestacaoTrabalho && (
              <div className="bg-gradient-to-r from-cyan-900/40 to-teal-900/40 backdrop-blur-sm rounded-2xl p-6 border border-cyan-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-teal-400 rounded-full flex items-center justify-center">
                    <span className="text-xl">💼</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Manifestação no Trabalho</h3>
                    <p className="text-white/50 text-xs">Seu arquétipo na vida profissional</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/85 leading-relaxed text-sm">{archetype.manifestacaoTrabalho}</p>
                </div>
              </div>
            )}

            {/* Caminho de Integração */}
            {archetype.caminhoIntegracao && (
              <div className="bg-gradient-to-r from-violet-900/40 to-purple-900/40 backdrop-blur-sm rounded-2xl p-6 border border-violet-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-400 to-purple-400 rounded-full flex items-center justify-center">
                    <span className="text-xl">🌱</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Caminho de Integração</h3>
                    <p className="text-white/50 text-xs">Direções para o desenvolvimento</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/85 leading-relaxed text-sm">{archetype.caminhoIntegracao}</p>
                </div>
              </div>
            )}

            {/* Reflexão Fenomenológica */}
            {archetype.reflexaoFenomenologica && (
              <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 backdrop-blur-sm rounded-2xl p-6 border border-indigo-500/20">
                <div className="text-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">💭</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Reflexão Final</h3>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/90 leading-relaxed italic text-center">{archetype.reflexaoFenomenologica}</p>
                </div>
              </div>
            )}

            {/* Práticas Analíticas */}
            {archetype.praticasAnaliticas && archetype.praticasAnaliticas.length > 0 && (
              <div className="bg-gradient-to-br from-teal-900/30 to-cyan-900/30 backdrop-blur-sm rounded-2xl p-6 border border-teal-500/20">
                <div className="text-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">🔬</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Práticas de Desenvolvimento</h3>
                  <p className="text-white/60 text-sm">Exercícios estruturados para trabalhar seu padrão</p>
                </div>
                <div className="space-y-3">
                  {archetype.praticasAnaliticas.map((pratica, index) => (
                    <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-teal-300 font-bold text-sm">{index + 1}</span>
                        </div>
                        <p className="text-white/90 leading-relaxed text-sm">{pratica}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ====== COMPONENTE 1: MAPA DAS ESTAÇÕES ====== */}
          <div ref={bussolaRef} className="rounded-2xl p-4" style={{ background: '#1e1b4b' }}>
            <MapaEstacoes result={result} />
          </div>

          {/* Botões Compartilhar Bússola */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-6 mb-4">
            <button
              onClick={handleDownloadBussola}
              disabled={sharingBussola}
              data-testid="download-bussola-btn"
              className="flex items-center gap-2.5 text-white border-none rounded-full px-8 py-4 text-base font-bold transition-all duration-300 tracking-wide hover:scale-105"
              style={{
                background: sharingBussola
                  ? 'rgba(245,158,11,0.4)'
                  : 'linear-gradient(135deg, #F59E0B, #EC4899)',
                boxShadow: '0 4px 24px rgba(245,158,11,0.3)',
                cursor: sharingBussola ? 'not-allowed' : 'pointer',
              }}
            >
              {sharingBussola ? (
                <>Gerando imagem...</>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Baixar minha Bússola
                </>
              )}
            </button>
            <button
              onClick={handleShareLink}
              data-testid="share-link-btn"
              className="flex items-center gap-2.5 text-white/90 border border-white/20 rounded-full px-8 py-4 text-base font-bold transition-all duration-300 tracking-wide hover:bg-white/10 hover:scale-105"
              style={{ cursor: 'pointer' }}
            >
              <Share2 className="w-5 h-5" />
              Compartilhar link
            </button>
          </div>

          {/* ====== COMPONENTE 1.5: DINÂMICA DAS ESTAÇÕES ====== */}
          <DinamicaEstacoes result={result} />

          {/* ====== COMPONENTE 2: PARA ONDE O CORAÇÃO APONTA ====== */}
          {(() => {
            const recommendations = getDeepRecommendations(result);
            if (!recommendations || recommendations.length === 0) return null;
            
            return (
              <div className="mt-8 mb-8" data-testid="recomendacoes-profundas">
                <div className="flex justify-center mb-6">
                  <div className="bg-gradient-to-r from-amber-900/40 via-orange-900/40 to-amber-900/40 px-6 py-3 rounded-full border border-amber-500/20">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-6 h-6 text-amber-400" />
                      <div className="text-center">
                        <h3 className="text-2xl md:text-3xl font-extrabold text-white">Para Onde o Coração Aponta</h3>
                        <p className="text-white/60 text-xs">O que o livro tem para despertar em você</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {recommendations.map((group, gi) => (
                    <div key={gi} className="rounded-2xl overflow-hidden border border-amber-500/15 bg-gradient-to-br from-amber-950/20 via-slate-900/30 to-orange-950/20" data-testid={`rec-group-${group.eixo}`}>
                      <div className="px-5 pt-5 pb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-2 h-2 rounded-full ${
                            group.fonte === 'tensao' ? 'bg-amber-400' :
                            group.fonte === 'tripla' ? 'bg-pink-400' :
                            group.fonte === 'equilibrio' ? 'bg-emerald-400' :
                            'bg-slate-400'
                          }`} />
                          <span className="text-amber-300 text-sm font-semibold">{group.direcao}</span>
                          {group.intensidade && (
                            <span className="text-amber-400/40 text-xs">({group.intensidade})</span>
                          )}
                        </div>
                        <p className="text-white/60 text-sm leading-relaxed">
                          {group.motivo || group.convite}
                        </p>
                      </div>
                      <div className="px-5 pb-5 space-y-4">
                        {group.chapters.map((chapter, ci) => {
                          const enriched = getChapterContent(chapter.capitulo);
                          return (
                            <div key={ci} className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-3">
                              <h4 className="text-white font-bold text-base" data-testid={`chapter-title-${gi}-${ci}`}>
                                {chapter.capitulo}
                              </h4>
                              {enriched ? (
                                <>
                                  <p className="text-amber-300/80 text-xs font-medium">{enriched.parte} {enriched.dominio && `— ${enriched.dominio}`}</p>
                                  <p className="text-white/80 text-sm italic leading-relaxed border-l-2 border-amber-500/30 pl-4">
                                    "{enriched.abertura}"
                                  </p>
                                  <p className="text-white/70 text-sm leading-relaxed">
                                    {enriched.promessa}
                                  </p>
                                  <a
                                    href={enriched.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 rounded-lg transition-all text-sm border border-amber-500/20"
                                  >
                                    <BookOpen className="w-4 h-4" />
                                    Ler este capítulo
                                  </a>
                                </>
                              ) : (
                                <>
                                  <p className="text-amber-300/70 text-xs">{chapter.parte} {chapter.bloco && `— ${chapter.bloco}`}</p>
                                  <p className="text-white/60 text-sm italic leading-relaxed">"{chapter.razao}"</p>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 text-center">
                  <a
                    href="https://ocoracaodetodasascoisas.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all"
                  >
                    <BookOpen className="w-5 h-5" />
                    Conhecer o Livro
                  </a>
                </div>
              </div>
            );
          })()}

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <button
            onClick={handleDownloadPDF}
            disabled={generatingPDF}
            data-testid="btn-download-pdf"
            className={`flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl transition-all ${generatingPDF ? 'opacity-50 cursor-wait' : 'hover:opacity-90'}`}
          >
            {generatingPDF ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Gerando...
              </>
            ) : (
              <>
                <Download size={20} />
                Baixar PDF
              </>
            )}
          </button>
          
          {result?.email && (
            <button
              onClick={() => setShowEvolucao(true)}
              data-testid="btn-evolucao"
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl hover:from-purple-600 hover:to-indigo-600 transition-all"
            >
              <History size={20} />
              Veja sua evolução
            </button>
          )}

          <Link
            to="/"
            data-testid="btn-novo-teste"
            className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all border border-white/20"
          >
            <ArrowLeft size={20} />
            Novo Teste
          </Link>
        </div>

        {/* Modal de Evolução */}
        {showEvolucao && (
          <EvolucaoEstacoes
            currentResult={result}
            email={result?.email}
            onClose={() => setShowEvolucao(false)}
          />
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-white/50 text-sm">
          <p>O Coração de Todas as Coisas</p>
          <p>www.ocoracaodetodasascoisas.com</p>
        </div>
      </div>
    </div>
  );
};

export default ResultadoPage;
